import { StandardEvents } from '@shopify/events';

const boutiqueRails = new Map();
const setupBoutiqueRails = () => {
  for (const [rail, cleanup] of boutiqueRails) {
    if (!rail.isConnected) { cleanup(); boutiqueRails.delete(rail); }
  }
  document.querySelectorAll('.hx-boutique-products').forEach(rail => {
    if (boutiqueRails.has(rail)) return;
    const controls = rail.closest('.hx-boutique-selection')?.querySelector('.hx-boutique-controls');
    if (!controls?.classList.contains('hx-boutique-controls')) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const sync = () => {
      if (!rail.clientWidth) return;
      const cards = [...rail.children];
      const bounds = rail.getBoundingClientRect();
      const rtl = getComputedStyle(rail).direction === 'rtl';
      const start = rtl ? bounds.right : bounds.left;
      const nearest = cards.reduce((best, card, index) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs((rtl ? rect.right : rect.left) - start);
        return distance < best.distance ? { index, distance } : best;
      }, { index:0, distance:Infinity });
      controls.hidden = rail.scrollWidth <= rail.clientWidth + 2;
      const first = cards[0]?.getBoundingClientRect();
      const last = cards.at(-1)?.getBoundingClientRect();
      controls.querySelector('[data-hx-boutique-step="-1"]').disabled = first ? (rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2) : true;
      controls.querySelector('[data-hx-boutique-step="1"]').disabled = last ? (rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2) : true;
      controls.querySelector('[data-hx-boutique-position]').textContent = `${nearest.index + 1} / ${cards.length}`;
    };
    const move = step => {
      const card = rail.firstElementChild;
      const distance = card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0) : rail.clientWidth;
      rail.scrollBy({left:step * distance * (getComputedStyle(rail).direction === 'rtl' ? -1 : 1), behavior:matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.hasAttribute('data-hx-reduce-motion') ? 'instant' : 'smooth'});
    };
    controls.addEventListener('click', event => { const button=event.target.closest('[data-hx-boutique-step]'); if(button && !button.disabled) move(Number(button.dataset.hxBoutiqueStep)); }, {signal});
    rail.addEventListener('keydown', event => { if(event.target !== rail || !['ArrowLeft','ArrowRight'].includes(event.key)) return; event.preventDefault(); const rtl=getComputedStyle(rail).direction === 'rtl'; move((event.key === 'ArrowLeft') === rtl ? 1 : -1); }, {signal});
    rail.addEventListener('scroll', sync, {passive:true, signal});
    const observer = new ResizeObserver(sync);
    observer.observe(rail);
    boutiqueRails.set(rail, () => { controller.abort(); observer.disconnect(); });
    sync();
  });
};
setupBoutiqueRails();
document.addEventListener('shopify:section:load', setupBoutiqueRails);
document.addEventListener('shopify:section:unload', event => {
  for (const [rail, cleanup] of boutiqueRails) {
    if (event.target.contains(rail)) { cleanup(); boutiqueRails.delete(rail); }
  }
});

let cartCountVersion = 0;
const renderHeaderCartCount = count => {
  if (!Number.isInteger(count) || count < 0) return;
  document.querySelectorAll('[data-hx-cart]').forEach(link => {
    const badge = link.querySelector('[data-hx-cart-count]');
    if (!badge) return;
    badge.textContent = String(count);
    badge.hidden = count === 0;
    link.setAttribute('aria-label', `${link.dataset.cartLabel} (${count})`);
  });
};
const refreshHeaderCartCount = async () => {
  const version = ++cartCountVersion;
  const link = document.querySelector('[data-hx-cart]');
  if (!link) return;
  try {
    const url = new URL(link.href);
    url.pathname = url.pathname.replace(/\/$/, '') + '.js';
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return;
    const cart = await response.json();
    if (version === cartCountVersion) renderHeaderCartCount(cart.item_count);
  } catch { /* Preserve the server-rendered count when offline. */ }
};
document.addEventListener(StandardEvents.cartLinesUpdate, async event => {
  const version = ++cartCountVersion;
  try {
    const result = await event.promise;
    if (version !== cartCountVersion) return;
    if (Number.isInteger(result?.cart?.totalQuantity)) renderHeaderCartCount(result.cart.totalQuantity);
    else await refreshHeaderCartCount();
  } catch { await refreshHeaderCartCount(); }
});
window.addEventListener('pageshow', refreshHeaderCartCount);
document.addEventListener('shopify:section:load', refreshHeaderCartCount);
refreshHeaderCartCount();
document.addEventListener('click', event => {
  const button = event.target.closest('[data-hx-carousel-step]');
  if (!button) return;
  const rail = button.closest('.hx-owner-carousel').querySelector('.hx-owner-rail');
  const rtl = getComputedStyle(rail).direction === 'rtl';
  const step = Number(button.dataset.hxCarouselStep);
  rail.scrollBy({ left: step * (rtl ? -1 : 1) * rail.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
});
const pending = new WeakMap();
document.addEventListener(StandardEvents.productSelect, async event => {
  const picker = event.target.closest('variant-picker');
  if (!picker) return;
  const promise = event.promise;
  pending.set(picker, promise);
  try {
    const { detail } = await promise;
    if (pending.get(picker) !== promise || !detail?.html) return;
    const scope = picker.closest('quick-add-dialog, product-information, featured-product-information, .shopify-section');
    if (!scope) return;
    // Product specifications are refreshed by hexo-product.js, which preserves
    // the disclosure state. This handler only owns purchase context.
    for (const selector of ['[data-hx-purchase-context]']) {
      const current = scope.querySelector(selector);
      const fresh = detail.html.querySelector(selector);
      if (current && fresh && current.dataset.productId === fresh.dataset.productId) {
        const disclosureStates = Array.from(current.querySelectorAll('details'), disclosure => disclosure.open);
        current.replaceChildren(...Array.from(fresh.childNodes, node => node.cloneNode(true)));
        current.querySelectorAll('details').forEach((disclosure, index) => {
          if (disclosureStates[index] !== undefined) disclosure.open = disclosureStates[index];
        });
      }
    }
    scope.querySelectorAll('[data-hx-quote]').forEach(link => { link.hidden = detail.resource?.price !== 0; });
  } catch (error) {
    if (error.name !== 'AbortError') console.warn('HEX-O product details could not refresh.');
  }
});





// Measure only the fixed header rows, never the viewport's changing browser chrome.
const ownerHeaderRows = [...document.querySelectorAll('.hx-header,.hx-announcement')];
const syncOwnerHeader = () => {
  const height = ownerHeaderRows.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0);
  if (height > 0) document.documentElement.style.setProperty('--hx-owner-header-height', height + 'px');
};
if (ownerHeaderRows.length) {
  const ownerHeaderObserver = new ResizeObserver(syncOwnerHeader);
  ownerHeaderRows.forEach(row => ownerHeaderObserver.observe(row));
  syncOwnerHeader();
}
